<script setup lang="ts">
import { watch } from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useWorkExperienceModalController } from '@/composables/current-student-advanced-standing-form/useWorkExperienceModalController'
import { maxUploadAttachments } from '@/constants/current-student-advanced-standing-form/upload'
import {
  buildWorkExperienceDeleteRequest,
  buildWorkExperienceEditPayload,
  workExperienceDeleteConfirmationMessage,
} from '@/models/current-student-advanced-standing-form/workExperienceModal'
import {
  clearModalAlert,
  showModalError,
  showModalInfo,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  FineUploaderUpload,
  StudentEditSubmissionRequest,
  StudentEditSubmissionResponse,
  StudentFormSubmission,
} from '@/models/current-student-advanced-standing-form/api'
import WorkExperienceModal from './WorkExperienceModal.vue'

type ConfirmDeleteAction = () => Promise<void>

const props = defineProps<{
  secureData: string
  modalIsShown: (modalId: string) => boolean
  modalIsActive: (modalId: string) => boolean
  syncModalVisibility: (modalId: string, visible: boolean) => void
  editStudentSubmission: (payload: StudentEditSubmissionRequest) => Promise<StudentEditSubmissionResponse>
  setSubmission: (submission: StudentFormSubmission) => void
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
  openConfirmDelete: (message: string, action: ConfirmDeleteAction) => void
  uploadHelpers: FineUploaderModalUploadHelpers
  triggerUploadInput: (event: MouseEvent) => void
  uploadButtonKeydown: (event: KeyboardEvent) => void
  setUploadButtonHover: (event: MouseEvent, hover: boolean) => void
  setUploadButtonFocus: (event: FocusEvent, focused: boolean) => void
  cancelStagedUpload: (attachment: FineUploaderUpload) => void
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const {
  workExperienceModalVisible,
  workExperienceSaving,
  workExperienceModalError,
  workExperienceModalAlertTone,
  currentWorkExperience,
  workExperienceYear,
  workExperienceShortDescription,
  workExperienceLongDescription,
  workExperienceAttachmentDeleteIds,
  workExperienceNewAttachments,
  workExperienceUploadsInProgress,
  workExperienceUploadDropActive,
  workExperienceUploadAlertMessage,
  workExperienceConfirmCloseWithoutSave,
  currentWorkExperienceAttachments,
  showWorkExperienceFineUploader,
  visibleWorkExperienceNewAttachments,
  openAddWorkExperienceModal,
  openEditWorkExperienceModal,
  closeWorkExperienceModal,
  onWorkExperienceLongInputChange,
  closeWorkExperienceUploadAlert,
  onWorkExperienceUploadDragEnter,
  onWorkExperienceUploadDragOver,
  onWorkExperienceUploadDragLeave,
  onWorkExperienceUploadDrop,
  onWorkExperienceAttachmentSelected,
  stageWorkExperienceAttachmentDelete,
  deleteStagedWorkExperienceAttachment,
  retryWorkExperienceAttachment,
} = useWorkExperienceModalController({
  maxAttachments: maxUploadAttachments,
  uploadHelpers: props.uploadHelpers,
})

watch(workExperienceModalVisible, (visible) => props.syncModalVisibility('WorkExperienceModal', visible))

async function saveWorkExperienceModal(): Promise<void> {
  if (workExperienceSaving.value || currentWorkExperience.value == null) return

  if (workExperienceUploadsInProgress.value > 0) {
    showModalError(workExperienceModalError, workExperienceModalAlertTone, 'Please wait for upload to complete')
    return
  }

  workExperienceSaving.value = true
  showModalInfo(workExperienceModalError, workExperienceModalAlertTone, 'Saving...')

  try {
    const workExperiencePayload = buildWorkExperienceEditPayload({
      existingWorkExperience: currentWorkExperience.value,
      workExperienceYear: workExperienceYear.value,
      workExperienceShortDescription: workExperienceShortDescription.value,
      workExperienceLongDescription: workExperienceLongDescription.value,
      deleteAttachmentIds: workExperienceAttachmentDeleteIds.value,
      newAttachments: workExperienceNewAttachments.value,
    })

    const result = await props.editStudentSubmission({
      secure_data: props.secureData,
      work_experience: workExperiencePayload,
    })
    workExperienceConfirmCloseWithoutSave.value = false
    props.setSubmission(result.data.submission)
    clearModalAlert(workExperienceModalError, workExperienceModalAlertTone)
    workExperienceModalVisible.value = false
  } catch (error) {
    if (props.handleAuthSessionError(error, 'reload')) return

    showModalError(workExperienceModalError, workExperienceModalAlertTone, props.getErrorMessage(error))
  } finally {
    workExperienceSaving.value = false
  }
}

function confirmWorkExperienceDelete(): void {
  const deleteId = currentWorkExperience.value?.submission_work_experience_id
  if (deleteId == null) return

  props.openConfirmDelete(workExperienceDeleteConfirmationMessage, async () => {
    const result = await props.editStudentSubmission(buildWorkExperienceDeleteRequest(props.secureData, deleteId))
    props.setSubmission(result.data.submission)
    workExperienceModalVisible.value = false
  })
}

defineExpose({
  openAddWorkExperienceModal,
  openEditWorkExperienceModal,
  closeWorkExperienceModal,
})
</script>

<template>
  <WorkExperienceModal
    :shown="modalIsShown('WorkExperienceModal')"
    :active="modalIsActive('WorkExperienceModal')"
    v-model:work-experience-year="workExperienceYear"
    v-model:work-experience-short-description="workExperienceShortDescription"
    v-model:work-experience-long-description="workExperienceLongDescription"
    :current-work-experience="currentWorkExperience"
    :current-work-experience-attachments="currentWorkExperienceAttachments"
    :work-experience-attachment-delete-ids="workExperienceAttachmentDeleteIds"
    :show-work-experience-fine-uploader="showWorkExperienceFineUploader"
    :visible-work-experience-new-attachments="visibleWorkExperienceNewAttachments"
    :work-experience-upload-drop-active="workExperienceUploadDropActive"
    :work-experience-upload-alert-message="workExperienceUploadAlertMessage"
    :error-message="workExperienceModalError"
    :alert-tone="workExperienceModalAlertTone"
    :saving="workExperienceSaving"
    :upload-thumbnail-url="uploadThumbnailUrl"
    @work-experience-long-input-change="onWorkExperienceLongInputChange"
    @stage-work-experience-attachment-delete="stageWorkExperienceAttachmentDelete"
    @work-experience-upload-drag-enter="onWorkExperienceUploadDragEnter"
    @work-experience-upload-drag-over="onWorkExperienceUploadDragOver"
    @work-experience-upload-drag-leave="onWorkExperienceUploadDragLeave"
    @work-experience-upload-drop="onWorkExperienceUploadDrop"
    @trigger-upload-input="triggerUploadInput"
    @upload-button-keydown="uploadButtonKeydown"
    @upload-button-hover="setUploadButtonHover"
    @upload-button-focus="setUploadButtonFocus"
    @work-experience-attachment-selected="onWorkExperienceAttachmentSelected"
    @cancel-staged-upload="cancelStagedUpload"
    @retry-work-experience-attachment="retryWorkExperienceAttachment"
    @delete-staged-work-experience-attachment="deleteStagedWorkExperienceAttachment"
    @close-work-experience-upload-alert="closeWorkExperienceUploadAlert"
    @delete="confirmWorkExperienceDelete"
    @close="closeWorkExperienceModal"
    @save="saveWorkExperienceModal"
  />
</template>
